import React from 'react';
import { Form, useChanges } from '../Form/Form';
import { useAsyncIndicator } from '../../util/util';
import { TextField } from '../Form/TextField/TextField';
import { Button } from '../Button/Button';
import { t } from '../../locales/translate';
import { loadContingent, shiftContingent, EditableSeatStatusType, loadAllocations } from '../../model/contingent';
import { Event, getEventSalesStatus } from '../../model/event';
import { useAsync } from '../../model/data';
import { Select } from '../Form/Select/Select';
import { SubmitLine } from '../SubmitLine/SubmitLine';
import { Line } from '../Line/Line';
import { Stack } from '../Stack/Stack';
import { Spacer } from '../Spacer/Spacer';

interface Props {
  section: { id: string };
  event: Event;
  onSubmit?: () => Promise<void>;
  onDeselect: () => void;
}

interface Params {
  pool: string | null;
  amount: string;
  status: EditableSeatStatusType;
  blockReason: string;
}

export function ContingentEditForm({ section, onDeselect, event, onSubmit }: Props): JSX.Element {
  const contingent = event.contingents?.find((e) => e.sectionId === section.id) ?? null;
  const [, setChanges, model, update] = useChanges<Params>({
    pool: null,
    amount: '0',
    status: 'blocked',
    blockReason: '',
  });
  const selectedPool = contingent?.pools?.find((e) => e.name === model.pool) ?? null;
  const [loading] = useAsync(async () => {
    await loadContingent(event, section.id);
  });

  const [submitting, execute] = useAsyncIndicator({ handleError: true });
  const canSubmit = !submitting && !loading;

  async function handleOnSubmit(): Promise<void> {
    await execute(
      shiftContingent({
        eventId: event.id,
        sectionId: section.id,
        amount: parseInt(model.amount),
        blockReason: model.status === 'blocked' ? model.blockReason : null,
        pool: model.pool,
      })
    );
    await Promise.all([
      loadContingent(event, section.id),
      loadAllocations(event),
      onSubmit?.(),
      getEventSalesStatus(event.id, { force: true }),
    ]);
  }

  return (
    <>
      <h3>{t.model.contingent.editContingent}</h3>
      <Spacer />
      <Form model={model} onInput={setChanges} onSubmit={handleOnSubmit}>
        <Stack>
          <Select
            name="pool"
            label={t.model.contingent.pool}
            allowEmpty={`${t.model.status.available} (${contingent?.available})`}
            options={contingent?.pools?.map((e) => ({ value: e.name, label: `${e.name} (${e.count})` })) ?? []}
            displayWithoutOptions
          />
          <Line>
            <TextField name="amount" label={t.general.amount} type="number" required />
            <Button
              title={t.general.all}
              size="small"
              disabled={loading}
              onClick={() =>
                update({ amount: (selectedPool?.count ?? contingent?.available ?? 0).toString() }, 'amount')
              }
            />
          </Line>
          <Select
            name="status"
            label={t.model.status._name}
            options={[
              { value: 'blocked', label: t.model.status.blocked },
              { value: 'available', label: t.model.status.available },
            ]}
            required
          />
          {model.status === 'blocked' && (
            <TextField name="blockReason" label={t.model.contingent.blockReason} required />
          )}
        </Stack>
        <SubmitLine>
          <Button title={t.actions.close('')} onClick={onDeselect} />
          <Button title={t.actions.save('')} type="primary" loading={submitting} disabled={!canSubmit} isSubmit />
        </SubmitLine>
      </Form>
    </>
  );
}
